class AnalyticsApi {
  constructor (pgConnection, agaveAdapter) {
    this.pgConnection = pgConnection
    this.agaveAdapter = agaveAdapter
  }

  async getRevenueSummary ({ fromDate, toDate, organisationId, location, productSku }) {
    checkForRequirements(this.pgConnection, location)
    const { fromDateUtc, toDateUtc, toDateExclusiveUtc, locationScope } = buildAnalyticsBaseContext({ fromDate, toDate, location })
    const { currentPeriodDays, previous3MonthsPeriodDays, params, whereClauses } = buildRevenueQueryContext({
      organisationId,
      fromDateUtc,
      toDateUtc,
      toDateExclusiveUtc,
      locationScope,
      productSku
    })

    const query = `
      WITH filtered AS (
        SELECT
          si.time_of_sale,
          si.units_sold::numeric AS units_sold,
          si.unit_price_at_sale
        FROM analytics.sales_indicators si
        INNER JOIN avocado.data_location dl
          ON dl.uuid = si.location_id
        WHERE organisation_id = $1
          AND si.time_of_sale >= $2::timestamptz
          AND si.time_of_sale < $3::timestamptz
          ${whereClauses.join('\n')}
      )
      SELECT
        COUNT(*) FILTER (WHERE time_of_sale >= $4::timestamptz AND time_of_sale < $5::timestamptz)::int AS total_rows,
        COUNT(*) FILTER (WHERE unit_price_at_sale IS NOT NULL AND time_of_sale >= $4::timestamptz AND time_of_sale < $5::timestamptz)::int AS priced_rows,
        COUNT(*) FILTER (WHERE unit_price_at_sale IS NULL AND time_of_sale >= $4::timestamptz AND time_of_sale < $5::timestamptz)::int AS missing_price_rows,
        COALESCE(SUM(units_sold) FILTER (WHERE time_of_sale >= $4::timestamptz AND time_of_sale < $5::timestamptz), 0)::numeric AS units_sold_total,
        COALESCE(
          SUM(units_sold * unit_price_at_sale) FILTER (
            WHERE unit_price_at_sale IS NOT NULL
              AND time_of_sale >= $4::timestamptz
              AND time_of_sale < $5::timestamptz
          ),
          0
        )::numeric AS revenue_total,
        COALESCE(
          SUM(units_sold * unit_price_at_sale) FILTER (
            WHERE unit_price_at_sale IS NOT NULL
              AND time_of_sale >= $2::timestamptz
              AND time_of_sale < $4::timestamptz
          ),
          0
        )::numeric AS previous_3_months_revenue_total
      FROM filtered
    `

    const { rows } = await this.pgConnection.query(query, params)
    const row = rows[0] || {}
    const revenueTotal = Number(row.revenue_total || 0)
    const previous3MonthsRevenueTotal = Number(row.previous_3_months_revenue_total || 0)
    const currentAverageDailyRevenue = revenueTotal / currentPeriodDays
    const previous3MonthsAverageDailyRevenue = previous3MonthsRevenueTotal / previous3MonthsPeriodDays
    const revenueDeltaPercent = calculateDeltaPct(currentAverageDailyRevenue, previous3MonthsAverageDailyRevenue)

    return {
      fromDate,
      toDate,
      organisationId,
      location: locationScope.location,
      productSku: productSku || null,
      totalRows: Number(row.total_rows || 0),
      pricedRows: Number(row.priced_rows || 0),
      missingPriceRows: Number(row.missing_price_rows || 0),
      unitsSoldTotal: Number(row.units_sold_total || 0),
      revenueTotal: revenueTotal,
      previous3MonthsAverageDailyRevenue: roundTo2(previous3MonthsAverageDailyRevenue),
      currentAverageDailyRevenue: roundTo2(currentAverageDailyRevenue),
      revenueDeltaPercent: revenueDeltaPercent
    }
  }

  async getRevenueByLocationTable ({ fromDate, toDate, organisationId, location, productSku }) {
    checkForRequirements(this.pgConnection, location)
    const { fromDateUtc, toDateUtc, toDateExclusiveUtc, locationScope } = buildAnalyticsBaseContext({ fromDate, toDate, location })
    const { currentPeriodDays, previous3MonthsPeriodDays, params, whereClauses } = buildRevenueQueryContext({
      organisationId,
      fromDateUtc,
      toDateUtc,
      toDateExclusiveUtc,
      locationScope,
      productSku
    })
    const grouping = getLocationTableGrouping(locationScope.location)

    const query = `
      WITH filtered AS (
        SELECT
          ${grouping.scopeExpression} AS location_fsid,
          si.time_of_sale,
          si.units_sold::numeric AS units_sold,
          si.unit_price_at_sale
        FROM analytics.sales_indicators si
        INNER JOIN avocado.data_location dl
          ON dl.uuid = si.location_id
        WHERE organisation_id = $1
          AND si.time_of_sale >= $2::timestamptz
          AND si.time_of_sale < $3::timestamptz
          ${whereClauses.join('\n')}
      )
      SELECT
        location_fsid,
        revenue_total,
        previous_3_months_revenue_total,
        CASE
          WHEN SUM(revenue_total) OVER () = 0::numeric THEN 0::numeric
          ELSE (revenue_total / SUM(revenue_total) OVER ()) * 100
        END AS percent_of_total
      FROM (
        SELECT
          location_fsid,
          COALESCE(
            SUM(units_sold * unit_price_at_sale) FILTER (
              WHERE unit_price_at_sale IS NOT NULL
                AND time_of_sale >= $4::timestamptz
                AND time_of_sale < $5::timestamptz
            ),
            0
          )::numeric AS revenue_total,
          COALESCE(
            SUM(units_sold * unit_price_at_sale) FILTER (
              WHERE unit_price_at_sale IS NOT NULL
                AND time_of_sale >= $2::timestamptz
                AND time_of_sale < $4::timestamptz
            ),
            0
          )::numeric AS previous_3_months_revenue_total
        FROM filtered
        WHERE location_fsid IS NOT NULL
        GROUP BY location_fsid
      ) grouped
      ORDER BY revenue_total DESC, location_fsid ASC
    `

    const { rows } = await this.pgConnection.query(query, params)
    return {
      fromDate: fromDate,
      toDate: toDate,
      organisationId: organisationId,
      location: locationScope.location,
      productSku: productSku || null,
      rows: rows.map((row) => ({
        locationFsid: row.location_fsid,
        revenueTotal: Number(row.revenue_total || 0),
        percentOfTotal: Number(row.percent_of_total || 0),
        previous3MonthsAverageDailyRevenue: roundTo2(Number(row.previous_3_months_revenue_total || 0) / previous3MonthsPeriodDays),
        currentAverageDailyRevenue: roundTo2(Number(row.revenue_total || 0) / currentPeriodDays),
        revenueDeltaPercent: calculateDeltaPct(
          Number(row.revenue_total || 0) / currentPeriodDays,
          Number(row.previous_3_months_revenue_total || 0) / previous3MonthsPeriodDays
        )
      }))
    }
  }

  async getRevenueByProductTable ({ fromDate, toDate, organisationId, location, productSku }) {
    checkForRequirements(this.pgConnection, location)
    const { fromDateUtc, toDateUtc, toDateExclusiveUtc, locationScope } = buildAnalyticsBaseContext({ fromDate, toDate, location })
    const { currentPeriodDays, previous3MonthsPeriodDays, params, whereClauses } = buildRevenueQueryContext({
      organisationId,
      fromDateUtc,
      toDateUtc,
      toDateExclusiveUtc,
      locationScope,
      productSku
    })

    const query = `
      WITH filtered AS (
        SELECT
          si.product_sku_id,
          si.time_of_sale,
          si.units_sold::numeric AS units_sold,
          si.unit_price_at_sale
        FROM analytics.sales_indicators si
        INNER JOIN avocado.data_location dl
          ON dl.uuid = si.location_id
        WHERE organisation_id = $1
          AND si.time_of_sale >= $2::timestamptz
          AND si.time_of_sale < $3::timestamptz
          ${whereClauses.join('\n')}
      )
      SELECT
        f.product_sku_id AS product_sku,
        COALESCE(ps.display_name, f.product_sku_id) AS product_name,
        COALESCE(
          SUM(f.units_sold) FILTER (
            WHERE f.units_sold IS NOT NULL
              AND f.time_of_sale >= $4::timestamptz
              AND f.time_of_sale < $5::timestamptz
          ),
          0
        )::numeric AS units_sold_total,
        COALESCE(
          SUM(f.units_sold) FILTER (
            WHERE f.unit_price_at_sale IS NOT NULL
              AND f.time_of_sale >= $4::timestamptz
              AND f.time_of_sale < $5::timestamptz
          ),
          0
        )::numeric AS units_sold_with_price,
        COALESCE(
          SUM(f.units_sold) FILTER (
            WHERE f.unit_price_at_sale IS NULL
              AND f.time_of_sale >= $4::timestamptz
              AND f.time_of_sale < $5::timestamptz
          ),
          0
        )::numeric AS units_sold_without_price,
        COALESCE(
          SUM(f.units_sold * f.unit_price_at_sale) FILTER (
            WHERE f.unit_price_at_sale IS NOT NULL
              AND f.time_of_sale >= $4::timestamptz
              AND f.time_of_sale < $5::timestamptz
          ),
          0
        )::numeric AS revenue_total,
        COALESCE(
          SUM(f.units_sold * f.unit_price_at_sale) FILTER (
            WHERE f.unit_price_at_sale IS NOT NULL
              AND f.time_of_sale >= $2::timestamptz
              AND f.time_of_sale < $4::timestamptz
          ),
          0
        )::numeric AS previous_3_months_revenue_total
      FROM filtered f
      LEFT JOIN avocado.data_productsku ps
        ON ps.old_sku_id = f.product_sku_id
      WHERE f.product_sku_id IS NOT NULL
      GROUP BY f.product_sku_id, ps.display_name
      HAVING COALESCE(
        SUM(f.units_sold) FILTER (
          WHERE f.units_sold IS NOT NULL
            AND f.time_of_sale >= $4::timestamptz
            AND f.time_of_sale < $5::timestamptz
        ),
        0
      )::numeric > 0::numeric
      ORDER BY revenue_total DESC, product_sku ASC
    `

    const { rows } = await this.pgConnection.query(query, params)
    return {
      fromDate: fromDate,
      toDate: toDate,
      organisationId: organisationId,
      location: locationScope.location,
      productSku: productSku || null,
      rows: rows.map((row) => ({
        productSku: row.product_sku,
        productName: row.product_name,
        unitsSoldTotal: Number(row.units_sold_total || 0),
        unitsSoldWithPrice: Number(row.units_sold_with_price || 0),
        unitsSoldWithoutPrice: Number(row.units_sold_without_price || 0),
        revenueTotal: Number(row.revenue_total || 0),
        previous3MonthsAverageDailyRevenue: roundTo2(Number(row.previous_3_months_revenue_total || 0) / previous3MonthsPeriodDays),
        currentAverageDailyRevenue: roundTo2(Number(row.revenue_total || 0) / currentPeriodDays),
        revenueDeltaPercent: calculateDeltaPct(
          Number(row.revenue_total || 0) / currentPeriodDays,
          Number(row.previous_3_months_revenue_total || 0) / previous3MonthsPeriodDays
        )
      }))
    }
  }

  fetchRevenueSummary (params) {
    return this.agaveAdapter.get('analytics/revenue/summary', params)
  }
}

const DATE_ONLY_REGEX = /^\d{4}-\d{2}-\d{2}$/
const LOCATION_SDP_REGEX = /:sdp:[^:]+$/i

function parseDateOnlyUtcOrThrow (value, fieldName) {
  if (!DATE_ONLY_REGEX.test(value)) {
    throw new Error(`${fieldName} must be in YYYY-MM-DD format`)
  }

  const date = new Date(`${value}T00:00:00.000Z`)
  if (Number.isNaN(date.getTime())) {
    throw new Error(`${fieldName} is not a valid date`)
  }

  if (date.toISOString().slice(0, 10) !== value) {
    throw new Error(`${fieldName} is not a valid date`)
  }

  return date
}

function resolveLocationScope (location) {
  if (location === 'national') {
    return {
      location: 'national',
      scopeType: 'national'
    }
  }

  const scopeType = LOCATION_SDP_REGEX.test(location) ? 'sdp' : 'regional'
  return {
    location: location,
    scopeType: scopeType
  }
}

function checkForRequirements (pgConnection, location) {
  if (!pgConnection) {
    const err = new Error('Postgres connection is required')
    err.status = 500
    throw err
  }

  if (!location) {
    const err = new Error('location is required')
    err.status = 400
    throw err
  }
}

function buildAnalyticsBaseContext ({ fromDate, toDate, location }) {
  const fromDateUtc = parseDateOnlyUtcOrThrow(fromDate, 'fromDate')
  const toDateUtc = parseDateOnlyUtcOrThrow(toDate, 'toDate')
  const toDateExclusiveUtc = new Date(toDateUtc)
  toDateExclusiveUtc.setUTCDate(toDateExclusiveUtc.getUTCDate() + 1)

  return {
    fromDateUtc: fromDateUtc,
    toDateUtc: toDateUtc,
    toDateExclusiveUtc: toDateExclusiveUtc,
    locationScope: resolveLocationScope(location)
  }
}

function buildRevenueQueryContext ({ organisationId, fromDateUtc, toDateUtc, toDateExclusiveUtc, locationScope, productSku }) {
  const currentMonthStartUtc = new Date(Date.UTC(fromDateUtc.getUTCFullYear(), fromDateUtc.getUTCMonth(), 1))
  const previous3MonthsStartUtc = new Date(Date.UTC(currentMonthStartUtc.getUTCFullYear(), currentMonthStartUtc.getUTCMonth() - 3, 1))
  const currentPeriodDays = calculateInclusiveDays(fromDateUtc, toDateUtc)
  const previous3MonthsPeriodDays = calculateInclusiveDays(previous3MonthsStartUtc, new Date(currentMonthStartUtc.getTime() - (24 * 60 * 60 * 1000)))
  const params = [
    organisationId,
    previous3MonthsStartUtc.toISOString(),
    toDateExclusiveUtc.toISOString(),
    currentMonthStartUtc.toISOString(),
    toDateExclusiveUtc.toISOString()
  ]
  const whereClauses = []

  appendScopeAndProductFilters({ params, whereClauses, locationScope, productSku })

  return {
    currentPeriodDays: currentPeriodDays,
    previous3MonthsPeriodDays: previous3MonthsPeriodDays,
    params: params,
    whereClauses: whereClauses
  }
}

function appendScopeAndProductFilters ({ params, whereClauses, locationScope, productSku }) {
  if (locationScope.scopeType !== 'national') {
    const exactIndex = params.length + 1
    const descendantIndex = params.length + 2
    whereClauses.push(`
        AND (
          dl.fsid = $${exactIndex}
          OR dl.fsid LIKE $${descendantIndex}
        )
    `)
    params.push(locationScope.location, `${locationScope.location}:%`)
  }

  if (productSku) {
    whereClauses.push(`AND si.product_sku_id = $${params.length + 1}`)
    params.push(productSku)
  }
}

function getLocationTableGrouping (location) {
  if (location === 'national') {
    return {
      groupByLevel: 'state',
      scopeExpression: `substring(dl.fsid from '^(.*:state:[^:]+)')`
    }
  }

  if (/:state:[^:]+$/i.test(location)) {
    return {
      groupByLevel: 'lga',
      scopeExpression: `substring(dl.fsid from '^(.*:lga:[^:]+)')`
    }
  }

  if (/:lga:[^:]+$/i.test(location)) {
    return {
      groupByLevel: 'sdp',
      scopeExpression: `substring(dl.fsid from '^(.*:sdp:[^:]+)')`
    }
  }

  const err = new Error('location must be national, state or lga scope for revenue location table')
  err.status = 400
  throw err
}

function calculateInclusiveDays (fromDateUtc, toDateUtc) {
  const msPerDay = 24 * 60 * 60 * 1000
  return Math.floor((toDateUtc.getTime() - fromDateUtc.getTime()) / msPerDay) + 1
}

function calculateDeltaPct (currentValue, baselineValue) {
  if (baselineValue === 0) {
    return null
  }
  return roundTo2(((currentValue - baselineValue) / baselineValue) * 100)
}

function roundTo2 (value) {
  return Math.round(value * 100) / 100
}

module.exports = AnalyticsApi
